/* ============================================================
   SINGSPACE ADMIN - PAGE VISUALISATION PUBLIQUE DEVIS
   Accessible via lien sécurisé (token) sans authentification
   ============================================================ */

import { AlertCircle, Building2, Calendar, CheckCircle, FileText, Loader2, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { cn } from '../../lib/utils';

// ============================================================
// COMPOSANT : DocumentViewer
// Affiche un PDF en blob via iframe avec gestion mémoire
// ============================================================
interface DocumentViewerProps {
  document: {
    url?: string;
    data?: string;
    type?: string;
    name?: string;
    documentKey?: string;
  };
  requiredDocuments?: Array<{ key: string; label: string }>;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, requiredDocuments = [] }) => {
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Trouver le label du document demandé
  const getDocumentLabel = () => {
    if (!document.documentKey) return null;
    const required = requiredDocuments.find((rd) => rd.key === document.documentKey);
    return required?.label;
  };

  const documentLabel = getDocumentLabel();
  const displayName = documentLabel || document.name || 'Document';

  useEffect(() => {
    const createObjectUrl = () => {
      try {
        const raw = document.url || document.data || '';
        if (!raw) {
          setError('Aucune donnée disponible');
          return;
        }

        // Convertir en data URL si nécessaire
        const dataUrl = raw.startsWith('data:')
          ? raw
          : `data:${document.type || 'application/pdf'};base64,${raw.replace(/\s/g, '')}`;

        // Extraire MIME type et base64
        const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
          setError('Format de données invalide');
          return;
        }

        const mimeType = matches[1];
        const base64 = matches[2];

        // Convertir en Blob puis Object URL
        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch (err) {
        console.error('[DocumentViewer] Error creating object URL:', err);
        setError('Erreur lors du chargement du document');
      }
    };

    createObjectUrl();

    // Nettoyage mémoire au démontage
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [document]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <Loader2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Chargement du document...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-bold text-gray-800">{displayName}</span>
          {documentLabel && document.name && <span className="text-xs text-gray-500">Fichier : {document.name}</span>}
        </div>
        <a
          href={objectUrl}
          download={document.name || 'document.pdf'}
          className="text-sm text-[#00758d] hover:text-[#005a6d] font-medium"
        >
          Télécharger
        </a>
      </div>
      <iframe
        src={objectUrl}
        className="w-full h-[600px]"
        title={document.name || 'Document'}
        style={{ border: 'none' }}
      />
    </div>
  );
};

export function QuoteViewPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<any>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!id || !token) {
        setError('Lien invalide');
        setLoading(false);
        return;
      }

      try {
        const API_URL = import.meta.env.VITE_API_URL || '/api';
        const response = await fetch(`${API_URL}/quotes/${id}/view?token=${token}`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Erreur lors du chargement du devis');
        }

        const result = await response.json();
        setQuote(result.data);
      } catch (err: any) {
        console.error('[QuoteView] Error:', err);
        setError(err.message || 'Erreur lors du chargement du devis');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Chargement du devis" submessage="Veuillez patienter..." size="md" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h1>
          <p className="text-gray-600 mb-6">{error || 'Lien invalide ou expiré'}</p>
          <p className="text-sm text-gray-500">
            Veuillez contacter l'administrateur si vous pensez qu'il s'agit d'une erreur.
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' XAF';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-[#00758d] to-[#005a6d] px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Devis #{quote.reference}</h1>
                <p className="text-blue-100">{quote.spaceType?.name || 'Espace non défini'}</p>
              </div>
              <div className="text-right">
                <div
                  className={cn(
                    'inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold',
                    quote.status === 'QUOTED' && 'text-white',
                    quote.status === 'PENDING' && 'text-[#00303c]',
                    quote.status === 'CONFIRMED' && 'text-white',
                  )}
                  style={{
                    backgroundColor:
                      quote.status === 'QUOTED'
                        ? '#a4006c'
                        : quote.status === 'PENDING'
                          ? '#dfc32f'
                          : quote.status === 'CONFIRMED'
                            ? '#00758d'
                            : '#5c4738',
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {quote.status === 'QUOTED' && 'Devis envoyé'}
                  {quote.status === 'PENDING' && 'En attente'}
                  {quote.status === 'CONFIRMED' && 'Confirmé'}
                </div>
              </div>
            </div>
          </div>

          {/* Informations client */}
          <div className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-[#00758d]" />
              Informations client
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nom</p>
                <p className="font-medium text-gray-900">{quote.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{quote.customerEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="font-medium text-gray-900">{quote.customerPhone}</p>
              </div>
              {quote.companyName && (
                <div>
                  <p className="text-sm text-gray-500">Entreprise</p>
                  <p className="font-medium text-gray-900">{quote.companyName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Détails de la réservation */}
          <div className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-[#00758d]" />
              Détails de la réservation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Date de début</p>
                <p className="font-medium text-gray-900">{formatDate(quote.startDate)}</p>
              </div>
              {quote.endDate && (
                <div>
                  <p className="text-sm text-gray-500">Date de fin</p>
                  <p className="font-medium text-gray-900">{formatDate(quote.endDate)}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Nombre de participants</p>
                <p className="font-medium text-gray-900 flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  {quote.participantCount}
                </p>
              </div>
              {quote.eventType && (
                <div>
                  <p className="text-sm text-gray-500">Type d'événement</p>
                  <p className="font-medium text-gray-900">{quote.eventType}</p>
                </div>
              )}
            </div>
            {quote.eventObjectives && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Objectifs de l'événement</p>
                <p className="font-medium text-gray-900">{quote.eventObjectives}</p>
              </div>
            )}
          </div>

          {/* Montant total */}
          <div className="px-8 py-6 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Montant total</p>
                <p className="text-3xl font-bold text-[#00758d]">{formatAmount(quote.totalAmount)}</p>
              </div>
              <FileText className="w-12 h-12 text-gray-300" />
            </div>
          </div>
        </div>

        {/* Documents joints */}
        {quote.documents && quote.documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="px-8 py-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-[#00758d]" />
                Documents joints ({quote.documents.length})
              </h2>
            </div>
            <div className="px-8 py-6 space-y-6">
              {quote.documents.map((doc: any, index: number) => (
                <DocumentViewer
                  key={doc.id || index}
                  document={{
                    url: doc.fileUrl,
                    name: doc.fileName,
                    type: doc.mimeType,
                    documentKey: doc.documentKey,
                  }}
                  requiredDocuments={quote.spaceType?.requiredDocs || []}
                />
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>Pour toute question concernant ce devis, veuillez contacter l'administrateur.</p>
          <p className="mt-2">© {new Date().getFullYear()} SingSpace - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
}
